import { useEffect, useState } from 'react';
import { isApiConfigured, listCategories, listPosts } from './api';
import { BLOGS } from '../data/mockContent';
import { Category, PostSummary } from '../types/content';

/**
 * The three original articles live as markdown files under public/content and
 * predate the database. They are folded into the index so nothing that was
 * already published disappears once the API goes live.
 */
export const LEGACY_POSTS: PostSummary[] = BLOGS.map((blog) => ({
  id: `legacy-${blog.id}`,
  slug: blog.id,
  title: blog.title,
  excerpt: blog.excerpt,
  coverImage: `${process.env.PUBLIC_URL || ''}/images/${blog.id}.svg`,
  category: 'system-design',
  tags: blog.tags,
  status: 'published',
  authorName: blog.author,
  readingMinutes: 8,
  publishedAt: blog.date,
  // These articles predate the review workflow and never went through it.
  submittedAt: null,
  reviewedBy: null,
  reviewedAt: null,
  reviewNote: null,
  createdAt: blog.date,
  updatedAt: blog.date,
}));

export const isLegacySlug = (slug: string) => BLOGS.some((b) => b.id === slug);

export interface BlogIndex {
  posts: PostSummary[];
  categories: Category[];
  loading: boolean;
  /** Set when the API could not be reached; the legacy posts are shown instead. */
  error: string | null;
}

function withCounts(categories: Category[], posts: PostSummary[]): Category[] {
  return categories.map((category) => ({
    ...category,
    postCount: posts.filter((p) => p.category === category.slug).length,
  }));
}

/** Categories the legacy posts need, in case the API is unreachable. */
const FALLBACK_CATEGORIES: Category[] = [
  {
    id: 'fallback-system-design',
    slug: 'system-design',
    name: 'System Design',
    description: 'Architecture deep dives, scalability patterns and distributed systems.',
    order: 1,
    createdAt: '',
    updatedAt: '',
  },
];

export function useBlogIndex(): BlogIndex {
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const applyFallback = (message: string | null) => {
      if (cancelled) return;
      setPosts(LEGACY_POSTS);
      setCategories(withCounts(FALLBACK_CATEGORIES, LEGACY_POSTS));
      setError(message);
      setLoading(false);
    };

    if (!isApiConfigured) {
      applyFallback(null);
      return;
    }

    (async () => {
      try {
        const [apiPosts, apiCategories] = await Promise.all([
          listPosts(),
          listCategories().catch(() => [] as Category[]),
        ]);
        if (cancelled) return;

        // Newest first, with the database posts and the original markdown
        // articles interleaved by publish date.
        const merged = [...apiPosts, ...LEGACY_POSTS].sort((a, b) => {
          const da = a.publishedAt ? Date.parse(a.publishedAt) : 0;
          const db = b.publishedAt ? Date.parse(b.publishedAt) : 0;
          return db - da;
        });

        // Every section is shown, including empty ones — they are the site's
        // structure, and an empty one renders its own "no articles yet" state.
        const known = apiCategories.length ? apiCategories : FALLBACK_CATEGORIES;
        setPosts(merged);
        setCategories(withCounts(known, merged));
        setError(null);
        setLoading(false);
      } catch (e) {
        applyFallback(e instanceof Error ? e.message : 'Could not reach the blog API.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, categories, loading, error };
}
